import { html, nothing, LitElement } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { empty } from '@outbook/stubs/functions';
import { navigate } from '@local/navigate';
import { TypeIcon } from '@outbook/webcomponents-type-icon';
import styleComponent from './_style/type-link.style.js';
import { isEventClick, isKeyEnter, isKeySpace } from '@outbook/a11y';
import { resolveLocale } from '@outbook/resolve-locale';
import { getI18n } from './_i18n/i18n.js';

class ComponentTypeLink extends LitElement {
  static properties = {
    text: { type: String },
    label: { type: String },
    url: { type: String },
    icon: { type: String },
    icons: { type: Object },
    target: { type: String },
    title: { type: String, attribute: 'title-text' },
    generated: { type: Boolean },
    isButton: { type: Boolean, attribute: 'is-button' },
    isDisabled: { type: Boolean, attribute: 'is-disabled' }
  };

  static styles = styleComponent;

  constructor() {
    super();
    this.text = '';
    this.label = '';
    this.url = '';
    this.icon = null;
    this.icons = null;
    this.target = null;
    this.title = null;
    this.generated = false;
    this.isButton = false;
  }

  get resolvedLocale() {
    return resolveLocale(this);
  }

  _emit(originalEvent) {
    this.dispatchEvent(
      new CustomEvent('type-link-interaction', {
        bubbles: true,
        composed: true,
        detail: { originalEvent }
      })
    );
  }

  _handleInternal = ev => {
    if (
      isEventClick(ev) ||
      isKeyEnter(ev) ||
      (this.isButton && isKeySpace(ev))
    ) {
      ev.preventDefault();
      const interactionFn = this.url
        ? navigate({
            url: this.url,
            cb: ({ ev: originalEvent }) => {
              this._emit(originalEvent);
            }
          })
        : () => {
            this._emit(ev);
          };
      interactionFn(ev);
    }
  };

  _handleExternal = ev => {
    this._emit(ev);
  };

  focus() {
    const element = this.shadowRoot?.querySelector('[tabindex="0"]');
    if (element) {
      element.focus();
    }
  }

  _renderContent() {
    const iconTemplate = this.icon
      ? TypeIcon({
          icon: this.icon,
          icons: this.icons || undefined,
          extraClasses: 'type-link__icon'
        })
      : nothing;
    return html`
      ${iconTemplate}
      <span class="type-link__text">
        ${this.text || this.title || this.icon || ''}
      </span>
    `;
  }

  render() {
    const literals = getI18n(this.resolvedLocale);
    const mainClasses = classMap({
      'type-link': true,
      'type-link--generated': this.generated,
      'is-disabled': this.isDisabled
    });

    if (this.target) {
      return html`
        <a
          class="${mainClasses}"
          href="${this.url}"
          target="${ifDefined(this.target)}"
          rel="noreferrer noopener"
          title="${ifDefined(this.label || this.title || undefined)}"
          aria-label="${ifDefined(this.label || undefined)}"
          @click="${this._handleExternal}"
        >
          ${this._renderContent()}
          ${
            this.target === '_blank'
              ? html`<span class="sr-only"> (${literals.opensInNewTab})</span>`
              : nothing
          }
        </a>
      `;
    }

    return html`
      <span
        class="${mainClasses}"
        tabindex="${ifDefined(this.isDisabled ? undefined : '0')}"
        role="${this.isButton ? 'button' : 'link'}"
        title="${ifDefined(this.label || this.title || undefined)}"
        aria-label="${ifDefined(this.label || undefined)}"
        aria-disabled="${ifDefined(this.isDisabled ? 'true' : undefined)}"
        @click="${ifDefined(this.isDisabled ? undefined : this._handleInternal)}"
        @keydown="${ifDefined(this.isDisabled ? undefined : this._handleInternal)}"
      >
        ${this._renderContent()}
      </span>
    `;
  }
}

if (!customElements.get('outbook-type-link')) {
  customElements.define('outbook-type-link', ComponentTypeLink);
}

export function TypeLink(props) {
  const {
    text = '',
    extraClasses = '',
    url,
    isButton = false,
    icon = null,
    icons,
    target,
    title,
    generated = false,
    id,
    isDisabled = false,
    label = '',
    eventFn = empty
  } = props;

  return html`
    <outbook-type-link
      id="${ifDefined(id || undefined)}"
      class="${ifDefined(extraClasses || undefined)}"
      text="${ifDefined(text || undefined)}"
      label="${ifDefined(label || undefined)}"
      url="${ifDefined(url || undefined)}"
      icon="${ifDefined(icon || undefined)}"
      target="${ifDefined(target || undefined)}"
      ?is-disabled="${isDisabled}"
      title-text="${ifDefined(title || undefined)}"
      ?is-button="${isButton}"
      ?generated="${generated}"
      .icons="${icons}"
      @type-link-interaction="${eventFn}"
    ></outbook-type-link>
  `;
}
